import React, { useContext, useEffect, useState } from "react";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Fees } from "./Fees";
import { getCurrency } from "country-currency-map";
import {
  InputBox,
  InputWrapper,
  SelectedDropdownOption,
} from "../../../styles";
import { Button } from "../../../../../../components/common/Button";
import { usePopulateFormFields } from "../../../../../../app/hooks/usePopulateFormFields";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { CurrencySelector } from "../../../../../../components/common/CurrencySelector";
import Loader from "../../../../../../components/common/Loader";
import ErrorMessage from "../../../../../../components/ErrorMessage";
import {
  getDefaultExchangeRate,
  getExchangeRate,
  getInternationalTransferFee,
  getReceivingCountries,
  getReceivingCurrenciesByCountry,
} from "../../../api";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { setSettlementDetails } from "../../../SettlementPayoutSlice";
import { Controller, useForm } from "react-hook-form";
import { SUPPORTED_SETTLEMENT_INTERNATIONAL_SENDING_CURRENCIES, settlementCountryMapToCode } from "../../../../../Settlements/utils";
import { useParams } from "react-router-dom";

interface Props {
  setInputtedAmount?: (data: any) => void;
  minimumPayoutValue?: number;
  selectedSendingCurrency: string;
  intAmount?: number;
  setIntAmount?: (data: number) => void;
  setSelectedSendingCurrency: (data: string) => void;
  totalCharges?: number;
  setInternationalFee: any;
  internationalFee?: any;
  setTotalCharges?: (data: any) => void;
  selectedCountry: string;
  setSelectedCountry: (data: string) => void;
  rate?: any;
  sendingCountriesData: string[];
  setSendingCountriesData: (data: string[]) => void;

  setRate?: (data: any) => void;
  onNext: () => void;
}

type InternationalPayoutType = {
  beneficiaryCountry?: string;
  amount?: number;
  destinationCurrency?: string;
  totalAmount?: number;
  transferFee?: number;
  exchangeRate?: number;
  amountBeforeFees?: number;
  sourceCurrency?: string;
}

const MINIMUM_USD = 100;

const International = ({
  onNext,
  setSelectedSendingCurrency,
  totalCharges,
  sendingCountriesData,
  setSendingCountriesData,
}: Props) => {
  const { currencyCode } = useParams()
  const capitalizedCurrencyCode = currencyCode?.toUpperCase() || "";
  const [sendingCurrenciesData, setSendingCurrenciesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMinimumError, setShowMinimumError] = useState(false);
  const [showMaximumError, setShowMaximumError] = useState(false);
  const [balanceExceeded, setBalanceExceeded] = useState(false);

  const dispatch = useAppDispatch();
  const { setValue, control, watch, handleSubmit } = useForm<InternationalPayoutType>()
  const { wallets } = useAppSelector((state) => state.wallets);
  const getSavedSettlementDetails = useAppSelector((state) => state?.settlementPayout?.settlementDetails);

  const formData = watch()

  const [rate, setRate] = useState(null)

  const { onShowAlert } = useContext(NetworkErrorAlertContext);

  const walletBalance = wallets?.find((wallet) => wallet.currency === formData?.sourceCurrency);

  const balance = walletBalance?.settlementBalance;

  const [minimumRecipientAmount, setMinimumRecipientAmount] =
    useState(MINIMUM_USD);

  useEffect(() => {
    async function fetchTransactionFee() {
      try {
        await getInternationalTransferFee({
          destinationCountry: settlementCountryMapToCode[formData?.sourceCurrency] || settlementCountryMapToCode[capitalizedCurrencyCode], 
          sourceCountry: formData?.destinationCurrency,
        }).then((res) => {
          const { fees } = res;
          setValue('transferFee', fees)
        });
      } catch (error) {
        return error;
      }
    }

    if (formData?.destinationCurrency !== null && formData?.amount >= 100) {

      fetchTransactionFee();
    }
  }, [
    formData?.destinationCurrency, formData?.amount, formData?.sourceCurrency
  ]);

  useEffect(() => {
    if (formData?.sourceCurrency && (formData?.amount > balance)) {
      setBalanceExceeded(true);
      onShowAlert("", `The amount is more than your ${formData?.sourceCurrency} settlement balance`);
    } else {
      setBalanceExceeded(false);
    }
  }, [balance, formData?.amount, formData?.sourceCurrency]);

  const getRecCountries = async () => {
    setLoading(true);
    const data = await getReceivingCountries();

    if (data.length) {
      const formattedReceivingCountries = data.map(
        (sen) => sen.destinationcountries,
      );
      const receivingCountriesOptions = formattedReceivingCountries.map(
        (pay) => ({
          label: pay,
          value: pay,
        }),
      );
      setSendingCountriesData(receivingCountriesOptions);
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    getRecCountries();
  }, []);

  useEffect(() => {
    if (formData?.amount && formData?.amount < minimumRecipientAmount) {
      setShowMinimumError(true);
    } else {
      setShowMinimumError(false);
    }
  }, [formData?.amount]);

  const onSendCountryChange = (value: string) => {
    onSendCurChange(null);
  };

  const onSendCurChange = async (value: string) => {
    setValue('destinationCurrency', value)
    setSelectedSendingCurrency(value);
    setShowMinimumError(false);
    if (value && value !== "USD") {
      try {
        const ExcData = await getDefaultExchangeRate({
          destinationCurrency: value,
          sourceCurrency: "USD",
          amount: MINIMUM_USD,
        });
        const excAmount = ExcData.amount || 0;
        setMinimumRecipientAmount(Math.ceil(excAmount / 100));
      } catch (e) {
        setMinimumRecipientAmount(MINIMUM_USD);
      }
    } else {
      setMinimumRecipientAmount(MINIMUM_USD);
    }
  };

  const getRecCurr = async (country: string) => {
    setLoading(true);
    try {
      const data = await getReceivingCurrenciesByCountry({
        country: country,
      });

      if (data.length) {
        const formattedReceivingCurrencies = data.map(
          (sen) => sen.availableCurrency,
        );
        const receivingCurrenciesOptions = formattedReceivingCurrencies.map(
          (pay) => {
            let theFullName;
            if (pay === "GHS") {
              theFullName = "Ghana Cedis";
            } else if (pay === "TRY") {
              theFullName = "Turkish lira";
            } else {
              const regex = /\(([^)]+)\)/i;
              theFullName = getCurrency(pay)?.name;
              theFullName = theFullName?.replace(regex, "").trimEnd();
            }
            return {
              value: pay,
              fullName: theFullName,
              label: (
                <SelectedDropdownOption>
                  <li
                    className={`currency-flag currency-flag-${pay?.toLowerCase()}`}
                  />{" "}
                  {pay}
                </SelectedDropdownOption>
              ),
            };
          },
        );
        const filteredReceivingCurrenciesOptions = receivingCurrenciesOptions.filter(option => SUPPORTED_SETTLEMENT_INTERNATIONAL_SENDING_CURRENCIES.includes(option.value));
        setSendingCurrenciesData(filteredReceivingCurrenciesOptions);
      }
    } catch (err) {
      throw new Error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (formData.beneficiaryCountry && formData.beneficiaryCountry.length) {
      getRecCurr(formData.beneficiaryCountry);
    }
  }, [formData.beneficiaryCountry]);


  const exchangeRate = async () => {
    setLoading(true);
    try {
      const response = await getExchangeRate({
        sourceCurrency: formData?.sourceCurrency || capitalizedCurrencyCode,
        amount: formData?.amount,
        destinationCurrency: formData?.destinationCurrency,
      });

      const { rate } = response;
      setRate(response);
      setValue('amountBeforeFees', Number((1 / rate).toFixed(2)) * formData?.amount)
    } catch (error) {
      throw new Error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData?.amount >= 100 && formData?.destinationCurrency) {
      exchangeRate();
    }
  }, [formData?.amount, formData?.destinationCurrency, formData?.sourceCurrency]);

  const handleNext = () => {
    const beneficiaryPayload = {
      ...getSavedSettlementDetails,
      ...formData,
      processingFee: transactionTransferFee,
      exchangeRate: transactionExchangeRate,
      amountBeforeFees: formData.amountBeforeFees,
      totalAmount: totalAmountToBeCharged,
      sourceCurrency: formData.sourceCurrency || capitalizedCurrencyCode,
    };
    dispatch(setSettlementDetails(beneficiaryPayload));
    onNext();
  };

  usePopulateFormFields<InternationalPayoutType>(getSavedSettlementDetails, setValue);

  const transactionExchangeRate = Number((1 / rate?.rate).toFixed(2)) || formData?.exchangeRate || 0

  const totalTransferFee = (formData.transferFee || 0) * (transactionExchangeRate || 0);

  const transactionTransferFee = Number((totalTransferFee || 0).toFixed(2)) || formData?.transferFee || 0

  const totalAmountToBeCharged = Number((formData.amountBeforeFees + transactionTransferFee).toFixed(2)) || formData?.totalAmount || 0

  const isAmountGreaterThanBalance = formData?.sourceCurrency && totalAmountToBeCharged > balance;

  const handleSourceCurrencyChange = (value: string) => {
    setValue('sourceCurrency', value)
    setSelectedSendingCurrency(value);
  }


  return (
    <div>
      <Loader isLoading={loading} />
      <InputBox>
        <Controller
          name="beneficiaryCountry"
          control={control}
          rules={{ required: "Beneficiary country is required" }}
          render={({ field }) => {
            return (
              <CustomSelect
                options={sendingCountriesData}
                showSearch
                onChange={(value) => {
                  field.onChange(value);
                  onSendCountryChange(value);
                }}
                placeholder="Beneficiary country"
                $height="40px"
                $width="100%"
                lightBorder
                {...field}
              />
            );
          }}
        />
      </InputBox>
      <InputBox>
        <Controller
          name="amount"
          control={control}
          rules={{ required: "Amount is required" }}
          render={({ field }) => {
            return (
              <CurrencySelector
                placeholder="Amount"
                onSelectChange={(value) => onSendCurChange(value)}
                countries={sendingCurrenciesData?.map((currency) => ({
                  value: currency.value,
                  label: currency.label,
                }))}
                amount={field.value}
                setAmountValue={(value) => field.onChange(value)}
                height="40px"
                selectValue={formData.destinationCurrency}
                {...field}
              />
            );
          }}
        />

        {showMinimumError && formData?.destinationCurrency && (
          <ErrorMessage
            message={`Minimum amount: ${formData?.destinationCurrency} ${new Intl.NumberFormat().format(
              minimumRecipientAmount,
            )}`}
          />
        )}

        {showMaximumError && (
          <ErrorMessage
            message={`Maximum amount: ${formData?.destinationCurrency} ${new Intl.NumberFormat().format(
              minimumRecipientAmount * 5000,
            )}`}
          />
        )}
      </InputBox>

      {totalTransferFee ? (
        <InputBox>
          <Fees fee={transactionTransferFee} text="Transfer fee" currencySymbol={capitalizedCurrencyCode} />
        </InputBox>
      ) : null}


      {rate?.rate && (
        <InputBox>
          <Fees fee={transactionExchangeRate} text="Exchange rate" />
        </InputBox>
      )}

      {
        formData.amountBeforeFees && (
          <InputBox>
            <Controller
              name="totalAmount"
              control={control}
              render={({ field }) => {
                return (

                  <CurrencySelector
                    placeholder="Amount"
                    onSelectChange={(value) => handleSourceCurrencyChange(value)}
                    countries={[{ value: capitalizedCurrencyCode}]}
                    setAmountValue={(value) => field.onChange(value)}
                    height="40px"
                    selectValue={formData.sourceCurrency || capitalizedCurrencyCode}
                    {...field}

                    amount={
                      formData.amountBeforeFees
                        ? Number((formData.amountBeforeFees + totalTransferFee).toFixed(2))
                        : 0
                    }
                  />
                );
              }}
            />
            {balanceExceeded && (
              <ErrorMessage
                message={`The amount is more than your ${formData?.sourceCurrency} settlement balance`}
              />
            )}
          </InputBox>
        )}

      <InputWrapper>
        <Button
          label="Continue"
          onClick={handleSubmit(handleNext)}
          height="48px"
          disabled={
            loading ||
            !formData?.amount ||
            !formData?.destinationCurrency ||
            showMinimumError ||
            showMaximumError ||
            isAmountGreaterThanBalance
          }
        />
      </InputWrapper>
    </div>
  );
};

export default International;
